import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { MdKeyboardArrowDown } from "react-icons/md";
import { isCoarsePointer } from "../../hooks/useViewport";

/**
 * One collapsible nav group. Expanded sidebar (160px): clicking the header
 * toggles an inline accordion of children, pre-opened if the current route
 * is inside it. Collapsed sidebar (72px icon rail): hovering the header
 * previews the flyout, and clicking/tapping it *pins* the flyout open -
 * hover alone was a dead end on touch devices (no hover event ever fires)
 * and felt broken to click-first desktop users, since the icon did nothing
 * on click. Pinned state closes on an outside click/tap, on picking a
 * child item, or when the sidebar expands.
 *
 * The flyout is portaled to document.body (mirroring the pattern already
 * used by src/components/Help/FloatingVideoPlayer.jsx) rather than rendered
 * in place: Sidebar.jsx's own ancestors (.sidebar-nav-scroll's
 * overflowX:hidden, and the outer .sidebar div's overflow:hidden from both
 * an inline style and Layout.css) clip anything positioned outside the
 * 72px rail, which made the flyout invisible and unclickable even after
 * the click-to-pin fix. Portaling escapes those ancestors entirely; the
 * portaled panel's screen position is computed from the trigger's own
 * getBoundingClientRect() instead of relying on CSS containment. Styling
 * (white background, 12px radius, 0 4px 24px rgba(0,0,0,0.15) shadow)
 * still matches this app's existing popover look (MobileBottomNav.jsx's
 * "More" panel).
 */
function NavGroup({ group, collapsed, navItemStyle, labelStyle, iconSize }) {
  const location = useLocation();
  const hasActiveChild = group.items.some((item) => location.pathname.startsWith(item.to));
  const [open, setOpen] = useState(hasActiveChild);
  const [hovered, setHovered] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const flyoutRef = useRef(null);
  const Icon = group.icon;
  const flyoutVisible = collapsed && (hovered || pinnedOpen);
  // 44px rows for fingers; desktop keeps its compact rows.
  const rowMinHeight = isCoarsePointer() ? 44 : undefined;

  // Collapsing the sidebar's accordion state and expanding it (which should
  // drop any pinned flyout) are independent concerns - reset whichever
  // doesn't apply to the current width so neither leaks into the other.
  useEffect(() => {
    if (!collapsed) setPinnedOpen(false);
  }, [collapsed]);

  // Position the portaled flyout against the trigger's actual screen
  // position - recomputed whenever it opens, and on resize while it's open
  // (the sidebar itself doesn't reflow on scroll, so scroll-tracking isn't
  // needed here).
  useEffect(() => {
    if (!flyoutVisible || !containerRef.current) return undefined;
    const updatePosition = () => {
      const rect = containerRef.current.getBoundingClientRect();
      // Keep the whole flyout on screen: lower groups on a short landscape
      // viewport (~650px) used to open below the fold. Header 44 + rows 44 + padding.
      const estimatedHeight = 56 + group.items.length * 44;
      const maxTop = window.innerHeight - estimatedHeight - 8;
      setFlyoutPos({ top: Math.max(8, Math.min(rect.top, maxTop)), left: rect.right + 8 });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [flyoutVisible, group.items.length]);

  // Outside click/tap closes a pinned flyout - required for touch devices,
  // which have no "mouse leave" to fall back on. The portaled panel is no
  // longer a DOM descendant of containerRef, so a click inside it must be
  // checked against flyoutRef too, or it would register as "outside" and
  // self-close before the NavLink's own onClick/navigation runs.
  useEffect(() => {
    if (!pinnedOpen) return undefined;
    const handleOutside = (e) => {
      const insideTrigger = containerRef.current?.contains(e.target);
      const insideFlyout = flyoutRef.current?.contains(e.target);
      if (!insideTrigger && !insideFlyout) setPinnedOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [pinnedOpen]);

  const expandedLabelStyle = { ...labelStyle, opacity: 1, maxHeight: "none", pointerEvents: "auto", textAlign: "left" };

  // react-router's NavLink treats `to` as a prefix match by default (active
  // whenever the current path starts with it), not an exact match. That's
  // right for e.g. Subscription (/subscription) staying active on its own
  // /subscription/:id detail page. But within Inventory, "Items" links to
  // /inventory itself, which is also a literal path-prefix of every sibling
  // (/inventory/reports, /inventory/low-stock, ...) - so without correction
  // "Items" stayed highlighted no matter which sibling was actually open.
  // Only force an exact-match `end` on an item whose own `to` is a prefix
  // of one of its *configured* siblings, so unrelated groups/items keep the
  // normal (and often desired) prefix-matching behavior.
  const needsExactMatch = (item) =>
    group.items.some((other) => other !== item && other.to.startsWith(`${item.to}/`));

  return (
    <li
      ref={containerRef}
      style={{ position: "relative" }}
      // Mouse only: iOS fires emulated mouseenter on tap and never the matching
      // leave, which left the flyout stuck open. Touch uses tap-to-pin instead.
      onPointerEnter={(e) => collapsed && e.pointerType === "mouse" && setHovered(true)}
      onPointerLeave={(e) => collapsed && e.pointerType === "mouse" && setHovered(false)}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => (collapsed ? setPinnedOpen((p) => !p) : setOpen((o) => !o))}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (collapsed) setPinnedOpen((p) => !p);
            else setOpen((o) => !o);
          }
        }}
        style={{
          ...navItemStyle(hasActiveChild),
          justifyContent: collapsed ? "center" : "space-between",
          flexDirection: collapsed ? "column" : "row",
        }}
      >
        {collapsed ? (
          <>
            <Icon size={iconSize} />
            <span style={labelStyle}>{group.label}</span>
          </>
        ) : (
          <>
            <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Icon size={iconSize} />
              <span style={expandedLabelStyle}>{group.label}</span>
            </span>
            <MdKeyboardArrowDown
              size={16}
              style={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
                flexShrink: 0,
                marginRight: "4px",
              }}
            />
          </>
        )}
      </div>

      {/* Expanded sidebar: inline accordion */}
      {!collapsed && open && (
        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 4px 0" }}>
          {group.items.map((item) => (
            <li key={item.key}>
              <NavLink
                to={item.to}
                end={needsExactMatch(item)}
                style={({ isActive }) => ({
                  ...navItemStyle(isActive),
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  minHeight: rowMinHeight || "40px",
                  marginLeft: "14px",
                  padding: "8px 10px",
                })}
              >
                {/* Sub-item icons dropped in the expanded desktop accordion -
                    the group's own icon plus indentation already convey the
                    hierarchy, so a per-item icon here was redundant. Still
                    shown in the collapsed flyout below, where icons carry
                    more of the scanning load in that compact popup. */}
                <span style={expandedLabelStyle}>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}

      {/* Collapsed sidebar: hover-previews, click/tap-pins. Portaled to
          document.body so it escapes Sidebar.jsx's clipping ancestors
          (see the component doc comment above) - position is fixed and
          computed from the trigger's own bounding rect, not CSS layout. */}
      {flyoutVisible &&
        ReactDOM.createPortal(
          <div
            ref={flyoutRef}
            style={{
              position: "fixed",
              top: flyoutPos.top,
              left: flyoutPos.left,
              minWidth: "190px",
              background: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
              border: "1px solid #F3F4F6",
              zIndex: 2000,
              maxHeight: "calc(100dvh - 16px)",
              overflowY: "auto",
              padding: "6px 0",
            }}
          >
            <div
              style={{
                padding: "8px 14px",
                fontSize: "11px",
                fontWeight: 700,
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {group.label}
            </div>
            {group.items.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={needsExactMatch(item)}
                onClick={() => {
                  setPinnedOpen(false);
                  setHovered(false);
                }}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 14px",
                  minHeight: rowMinHeight,
                  fontSize: "13px",
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? "#C2185B" : "#374151",
                  background: isActive ? "rgba(194,24,91,0.07)" : "transparent",
                  textDecoration: "none",
                })}
              >
                <item.icon size={17} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>,
          document.body
        )}
    </li>
  );
}

export default NavGroup;
